// ---- special-filter parsing ----

export type ComparisonType = ":" | "<" | "<=" | ">=" | ">";

export interface SpecialFilter {
	keyword: string;
	comparison: ComparisonType;
	value: string;
}

const specialFilterRegex = /^(\w+)(<=|>=|<|>|:)(.+)$/;

/** Parse a single token as a special filter. Returns null if it doesn't match the special-filter syntax. */
export function ParseSpecialFilter(token: string): SpecialFilter|null {
	const match = token.match(specialFilterRegex);
	if (match == null) return null;
	return {keyword: match[1], comparison: match[2] as ComparisonType, value: match[3]};
}

// ---- time-unit parsing ----

export const timeUnitMap: Record<string, number> = {
	s: 1000,
	m: 60 * 1000,
	h: 60 * 60 * 1000,
	d: 24 * 60 * 60 * 1000,
	w: 7 * 24 * 60 * 60 * 1000,
};

/**
 * Parse a duration string like "1d", "2h", "30m" into milliseconds.
 * Returns null if the string is not a valid duration.
 */
export function ParseDurationString(str: string): number|null {
	const match = str.match(/^([0-9]*\.?[0-9]+)([smhdw])$/);
	if (match == null) return null;
	const amount = parseFloat(match[1]);
	const unit = match[2];
	return amount * timeUnitMap[unit];
}

/**
 * Given a "time ago" in ms and a unit character, round to the nearest integer number of that unit.
 * Returns null if the unit is unrecognised.
 */
export function RoundTimeAgoToUnit(timeAgo_ms: number, unit: string): number|null {
	const unitMs = timeUnitMap[unit];
	if (unitMs == null) return null;
	return Math.round(timeAgo_ms / unitMs);
}

/**
 * Check the "age" special filter.
 * @param createdAt  Unix timestamp in milliseconds
 * @param comparison The comparison operator.
 * @param valueStr   The value string, e.g. "1d", "2h".
 */
export function CheckAgeFilter(createdAt_ms: number, comparison: ComparisonType, valueStr: string): boolean {
	const unitMatch = valueStr.match(/^[0-9]*\.?[0-9]+([smhdw])$/);
	const unit = unitMatch ? unitMatch[1] : null;
	const durationMs = ParseDurationString(valueStr);
	if (durationMs == null) return true; // unparseable value – don't filter

	const now = Date.now();
	const objectAgeMs = now - createdAt_ms;

	if (comparison == ":") {
		// ":" means equals – compare rounded age (in units) to rounded value
		if (unit == null) return true;
		const roundedObjAge = RoundTimeAgoToUnit(objectAgeMs, unit);
		const roundedValue = RoundTimeAgoToUnit(durationMs, unit);
		if (roundedObjAge == null || roundedValue == null) return true;
		return roundedObjAge == roundedValue;
	}
	if (comparison == "<") return objectAgeMs < durationMs;
	if (comparison == "<=") return objectAgeMs <= durationMs;
	if (comparison == ">") return objectAgeMs > durationMs;
	if (comparison == ">=") return objectAgeMs >= durationMs;

	return true;
}